import { useEffect, useState } from "react";
import { Clock, TrendUp, Users } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Compact manager scorecard: shown at the top of the Team Approvals tab
 * and on the main Dashboard. Renders four stat tiles + a department burn-rate
 * bar chart. Non-blocking — silently hides on any load error.
 */
export default function LeaveAnalyticsCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/hr/analytics/leaves")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;

  const stat = (label, value, hint) => (
    <div className="border border-zinc-200 bg-white p-3">
      <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-400 uppercase">{label}</div>
      <div className="text-2xl font-black tracking-tighter mt-1 tnum" data-testid={`analytics-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
    </div>
  );

  const fmtHrs = (h) => {
    if (h == null) return "—";
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
  };
  const monthLabel = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-").map((s) => parseInt(s, 10));
    return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };

  return (
    <section className="mb-4" data-testid="leave-analytics-card">
      <div className="grid gap-2 md:grid-cols-4 mb-3">
        {stat("Pending", data.pending_count, "awaiting decision")}
        {stat("Decided this month", data.decided_this_month, "approved + rejected")}
        {stat("Avg approval", fmtHrs(data.avg_approval_hours),
              data.fastest_hours != null ? `⚡ ${fmtHrs(data.fastest_hours)} · 🐢 ${fmtHrs(data.slowest_hours)}` : "—")}
        {stat("Busiest month", monthLabel(data.most_taken_month?.ym),
              data.most_taken_month ? `${data.most_taken_month.days}d taken` : "no leaves yet")}
      </div>

      {data.dept_burn_rate && data.dept_burn_rate.length > 0 && (
        <div className="border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 px-3 py-2 flex items-center gap-2 bg-zinc-50">
            <TrendUp size={12} className="text-zinc-500" />
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">Dept burn rate (YTD)</div>
          </div>
          <div className="p-3 space-y-2" data-testid="dept-burn-rows">
            {data.dept_burn_rate.map((row) => (
              <div key={row.department} className="grid grid-cols-[1fr_50px_1fr_60px] gap-3 items-center text-xs" data-testid={`dept-burn-${row.department}`}>
                <div className="font-medium flex items-center gap-1.5">
                  <Users size={11} className="text-zinc-400" />
                  {row.department}
                  <span className="text-[10px] font-mono text-zinc-400">· {row.headcount}p</span>
                </div>
                <div className="tnum text-right text-zinc-600">{row.used}d</div>
                <div className="h-2 bg-zinc-100 relative">
                  <div
                    className={`absolute inset-y-0 left-0 ${row.pct > 75 ? "bg-[#D0021B]" : row.pct > 40 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(100, row.pct)}%` }}
                    title={`${row.used} of ${row.entitlement_total}d`}
                  />
                </div>
                <div className="tnum text-right font-semibold">{row.pct}%</div>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-200 px-3 py-1.5 flex items-center gap-4 text-[10px] font-mono text-zinc-500">
            <Clock size={11} />
            <span>as of {data.as_of}</span>
            <span className="ml-auto">green ≤40% · amber ≤75% · red &gt;75%</span>
          </div>
        </div>
      )}
    </section>
  );
}
